import React, { useState } from 'react';
import Navigation from './navigation';
import Footer from './footer';
import SmsConsent, { ORG_NAME, formatPhone, submitSmsOptIn } from './SmsConsent';
import './SmsOptIn.css';

const SmsOptIn = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    zipCode: '',
    phone: '',
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'phone' ? formatPhone(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!consent) {
      setError('Please check the box to agree to receive text messages.');
      return;
    }

    setLoading(true);
    try {
      await submitSmsOptIn({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        zipCode: formData.zipCode.trim(),
        phone: formData.phone,
      });

      setSuccess(
        "You're signed up! You'll start receiving text updates from our campaign. " +
          'Reply STOP at any time to cancel.'
      );
      setFormData({ firstName: '', lastName: '', email: '', zipCode: '', phone: '' });
      setConsent(false);
    } catch (err) {
      setError(err.message === 'Request failed' || !err.message
        ? 'Something went wrong. Please try again.'
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navigation />
      </div>

      <div className="hero-section2">
        <div className="page-title">Get Text Updates</div>
      </div>

      <div className="sms-optin-container">
        <div className="sms-optin-card">
          <p className="sms-optin-intro">
            Sign up to receive text messages from {ORG_NAME}. We'll keep you posted on
            events, endorsements, volunteer opportunities and how to make your voice heard
            at the ballot box.
          </p>

          <form className="sms-optin-form" onSubmit={handleSubmit}>
            <div className="sms-optin-row">
              <div className="sms-optin-field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="sms-optin-field">
                <label htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sms-optin-row">
              <div className="sms-optin-field">
                <label htmlFor="email">Email (optional)</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="sms-optin-field">
                <label htmlFor="zipCode">ZIP code (optional)</label>
                <input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sms-optin-field">
              <label htmlFor="phone">Mobile phone number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                inputMode="tel"
                placeholder="(248) 555-0123"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            {/* Directly below the phone field, unchecked by default, and required
                here because receiving texts is the whole point of this form. */}
            <SmsConsent checked={consent} onChange={setConsent} required />

            <button
              type="submit"
              className="btn-primary-primary sms-optin-submit"
              disabled={loading}
            >
              {loading ? 'Signing you up...' : 'Sign Me Up for Text Updates'}
            </button>

            {error && <div className="sms-optin-message sms-optin-error">{error}</div>}
            {success && <div className="sms-optin-message sms-optin-success">{success}</div>}
          </form>

          <div className="sms-optin-terms">
            <h2>Text Messaging Terms</h2>
            <ul>
              <li>
                <strong>Program:</strong> {ORG_NAME} sends periodic text messages to
                supporters who opt in, covering the message categories listed above.
              </li>
              <li>
                <strong>Message frequency:</strong> Varies. You may receive several
                messages per month, and more often near an election.
              </li>
              <li>
                <strong>Cost:</strong> Message and data rates may apply. Sign-up is free;
                your mobile carrier's standard rates apply.
              </li>
              <li>
                <strong>To opt out:</strong> Reply <strong>STOP</strong> to any message
                and you will stop receiving texts from us.
              </li>
              <li>
                <strong>For help:</strong> Reply <strong>HELP</strong> to any message, or
                email <a href="mailto:office@ren4misenate.com">office@ren4misenate.com</a>.
              </li>
              <li>
                <strong>Carriers:</strong> Wireless carriers are not liable for delayed or
                undelivered messages.
              </li>
              <li>
                <strong>Your privacy:</strong> Mobile information, including your phone
                number and opt-in consent, will not be shared with third parties or
                affiliates for marketing or promotional purposes. See our{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SmsOptIn;
